'use client';

import { Button } from '@smartproxy/decoro/v2/button';
import { Card } from '@smartproxy/decoro/v2/card';
import { Divider } from '@smartproxy/decoro/v2/divider';
import { Heading } from '@smartproxy/decoro/v2/heading';
import { Text } from '@smartproxy/decoro/v2/text';

import { cn } from '@/lib/utils';

import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';

import { PHTrackerProps } from '@/app/types';
import { phTrackerKey } from '@/integrations/trackingScripts/utils';

import type { BentoImageVariant } from './bentoImageVariant';
import { BentoInfocardBadge } from './BentoInfocardBadge';
import { getBentoCtaLinkProps } from './getBentoCtaLinkProps';

export type BentoFeatureCardBadge = {
  text: string;
  icon?: ImageStrapiProps;
};

export type BentoFeatureCardBullet = {
  text: string;
  icon: ImageStrapiProps;
};

export type BentoFeatureCardProps = {
  title: string;
  bodyText: RichTextFieldStrapiProps;
  ctaLabel?: string;
  ctaHref?: string;
  ctaTarget?: '_blank';
  badges?: BentoFeatureCardBadge[];
  bulletPoints?: BentoFeatureCardBullet[];
  illustration?: ImageStrapiProps;
  imageVariant?: BentoImageVariant;
  className?: string;
  onCtaClick?: () => void;
} & PHTrackerProps;

const IMAGE_WRAPPER_CLASS: Record<BentoImageVariant, string> = {
  curved: 'px-6',
  fullwidth: '',
  isolated: 'px-6 pb-6',
};

const IMAGE_CLASS: Record<BentoImageVariant, string> = {
  curved: 'h-36 w-full rounded-t-lg',
  fullwidth: 'h-40 w-full',
  isolated: 'h-32 w-full rounded-lg',
};

export const BentoFeatureCard = ({
  title,
  bodyText,
  ctaLabel,
  ctaHref,
  ctaTarget,
  [phTrackerKey]: phTracker,
  badges,
  bulletPoints,
  illustration,
  imageVariant = 'curved',
  className,
  onCtaClick,
}: BentoFeatureCardProps) => {
  const visibleBadges = badges?.slice(0, 2) ?? [];
  const hasBulletPoints = !!bulletPoints?.length;

  return (
    <Card
      theme='dark'
      className={cn(
        'flex h-full w-full flex-col justify-between overflow-clip rounded-2xl border-gray-dark-700 bg-gray-dark-900 pt-6',
        className
      )}
    >
      <div className={cn('flex flex-col gap-5 px-6', !illustration && 'pb-6')}>
        <div
          className={cn('flex flex-col', hasBulletPoints ? 'gap-8' : 'gap-5')}
        >
          <div className='flex flex-col gap-2'>
            <Heading variant='h5' skin='white'>
              {title}
            </Heading>
            <Text variant='body' skin='light' as='div'>
              <RichTextFieldStrapi
                block={bodyText}
                classNames={{
                  paragraph: 'm-0 text-inherit',
                  listUnordered: 'm-0 ml-0 list-disc gap-0 pl-5 text-inherit',
                  listOrdered: 'm-0 ml-0 list-decimal gap-0 pl-5 text-inherit',
                }}
              />
            </Text>
          </div>

          {hasBulletPoints && (
            <>
              <Divider className='border-gray-dark-600' />
              <ul className='flex w-full flex-col gap-5'>
                {bulletPoints.map((bullet) => (
                  <li
                    key={bullet.text}
                    className='flex w-full items-center gap-3'
                  >
                    <span
                      className={cn(
                        'flex size-8 shrink-0 items-center justify-center rounded-sm'
                      )}
                    >
                      <ImageStrapi
                        block={bullet.icon}
                        width={24}
                        height={24}
                        inner={{
                          classNames: {
                            container: 'size-6 min-w-0',
                            image: 'size-6',
                          },
                        }}
                      />
                    </span>
                    <Text
                      as='span'
                      variant='body_s'
                      skin='light'
                      className='min-w-0 flex-1'
                    >
                      {bullet.text}
                    </Text>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {ctaLabel && (
          <Button
            {...getBentoCtaLinkProps(ctaHref, ctaTarget, phTracker)}
            variant='outline'
            skin='dark'
            size='small'
            className={cn('w-full', !hasBulletPoints && 'md:w-fit')}
            onClick={onCtaClick}
          >
            {ctaLabel}
          </Button>
        )}

        {visibleBadges.length > 0 && (
          <div className='flex flex-wrap gap-5'>
            {visibleBadges.map((badge) => (
              <BentoInfocardBadge key={badge.text} {...badge} />
            ))}
          </div>
        )}
      </div>

      {illustration && (
        <div className={cn('mt-5', IMAGE_WRAPPER_CLASS[imageVariant])}>
          <ImageStrapi
            block={illustration}
            width={316}
            height={135}
            inner={{
              objectFit: 'cover',
              classNames: {
                container: 'w-full min-w-0',
                image: IMAGE_CLASS[imageVariant],
              },
            }}
          />
        </div>
      )}
    </Card>
  );
};
